/**
 * EditReservationDrawer — pannello laterale destro per la modifica completa
 * di una prenotazione esistente. Riutilizzato da Lista, Vista Giorno/Settimana
 * e Gestione Servizio.
 *
 * Caratteristiche:
 * - Tutti i campi editabili nella stessa vista (customer, date/time/pax, zone,
 *   table, occasion, notes, allergies, marketing).
 * - CRM lookup live sul telefono (endpoint /admin/customers/lookup) — mai
 *   duplica il cliente esistente.
 * - Cambio stato con conferma se serve override (force).
 * - Timeline unificata: status_history + updates_history.
 * - Preserva reservation ID: PATCH sullo stesso record.
 */
import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { hasPermission } from "@/lib/permissions";
import { toast } from "sonner";
import ReservationNotificationCard from "@/components/reservations/ReservationNotificationCard";
import ZonePicker, { ZONES } from "@/components/reservations/ZonePicker";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Save, Trash2, Send, Copy as CopyIcon, User, Phone, Mail,
  CalendarDays, Clock, Users, MapPin, Hash, Sparkles, StickyNote,
  History, Loader2, AlertTriangle, ExternalLink,
} from "lucide-react";

const STATUS_STYLES = {
  da_confermare: "bg-amber-100 text-amber-900 border-amber-300",
  confermata:    "bg-emerald-100 text-emerald-900 border-emerald-300",
  arrivato:      "bg-sky-100 text-sky-900 border-sky-300",
  completata:    "bg-neutral-200 text-neutral-800 border-neutral-300",
  annullata:     "bg-rose-100 text-rose-900 border-rose-300",
  no_show:       "bg-orange-100 text-orange-900 border-orange-300",
};

const STATUS_LABELS = {
  da_confermare: "Da confermare", confermata: "Confermata",
  arrivato: "Arrivato", completata: "Completata",
  annullata: "Annullata", no_show: "No-show",
};

const FIELD_LABELS = {
  customer_name: "Nome", customer_surname: "Cognome",
  customer_phone: "Telefono", customer_email: "Email",
  date: "Data", time: "Ora", pax: "Coperti",
  duration_min: "Durata", zone: "Zona", table_number: "Tavolo",
  occasion: "Occasione", notes: "Note", allergies: "Allergie",
  booking_source: "Origine", customer_id: "Cliente CRM",
};

const fmtVal = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (Array.isArray(v)) return v.join(", ") || "—";
  return String(v);
};
const fmtRome = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("it-IT", { timeZone: "Europe/Rome",
    day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};
const fmtWhen = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("it-IT",
    { timeZone: "Europe/Rome", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

export default function EditReservationDrawer({ id, cfg, onClose, onChanged }) {
  const { user } = useAuth();
  const canEdit = hasPermission(user, "booking.edit");
  const canCancel = hasPermission(user, "booking.cancel");
  const canDelete = ["admin", "super_admin"].includes(user?.role);
  const [r, setR] = useState(null);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [crmMatch, setCrmMatch] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/reservations/${id}`);
      setR(res.data);
      setForm({
        customer_name: res.data.customer_name || "",
        customer_surname: res.data.customer_surname || "",
        customer_phone: res.data.customer_phone || "",
        customer_email: res.data.customer_email || "",
        date: res.data.date || "",
        time: res.data.time || "",
        pax: res.data.pax || 1,
        duration_min: res.data.duration_min || 120,
        zone: res.data.zone || "",
        table_number: res.data.table_number || "",
        occasion: res.data.occasion || "",
        notes: res.data.notes || "",
        allergies: (res.data.allergies || []).join(", "),
        booking_source: res.data.booking_source || "",
      });
    } catch (e) { toast.error(formatApiError(e)); onClose(); }
    finally { setLoading(false); }
  }, [id, onClose]);

  useEffect(() => { load(); }, [load]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  // Debounced CRM lookup on phone change.
  const debounceRef = useRef(null);
  useEffect(() => {
    if (!form?.customer_phone) { setCrmMatch(null); return; }
    if (form.customer_phone === r?.customer_phone) { setCrmMatch(null); return; }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await api.get(
          `/admin/customers/lookup?phone=${encodeURIComponent(form.customer_phone)}`);
        setCrmMatch(res.data?.found ? res.data : null);
      } catch { setCrmMatch(null); }
    }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [form?.customer_phone, r?.customer_phone]);

  const applyCrmPrefill = () => {
    if (!crmMatch) return;
    setForm((f) => ({
      ...f,
      customer_name: f.customer_name || crmMatch.name || "",
      customer_surname: f.customer_surname || crmMatch.surname || "",
      customer_email: f.customer_email || crmMatch.email || "",
    }));
    toast.success(`Cliente CRM riusato: ${crmMatch.name} ${crmMatch.surname || ""}`);
  };

  const save = async (force = false) => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        pax: Number(form.pax) || 1,
        duration_min: Number(form.duration_min) || 120,
        allergies: (form.allergies || "").split(",").map((x) => x.trim()).filter(Boolean),
      };
      const url = `/admin/reservations/${id}${force ? "?force=true" : ""}`;
      await api.patch(url, payload);
      toast.success("Prenotazione aggiornata");
      onChanged && onChanged();
      onClose();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.error === "double_booking") {
        if (window.confirm(
          `Tavolo occupato:\n${detail.conflicts.map((c) => `${c.code} · ${c.customer_name} · ${c.time}`).join("\n")}\n\nSalvare comunque?`)) {
          save(true); return;
        }
      } else {
        toast.error(formatApiError(e));
      }
    } finally { setSaving(false); }
  };

  const changeStatus = async (to) => {
    const currentStatus = r?.status;
    if (!currentStatus || to === currentStatus) return;
    // Force is always safe for correction; also confirmed by the user.
    if (!window.confirm(`Cambiare stato in "${STATUS_LABELS[to]}"?`)) return;
    try {
      await api.patch(`/admin/reservations/${id}/status`,
                        { status: to, force: true });
      toast.success(`Stato: ${STATUS_LABELS[to]}`);
      load();
      onChanged && onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async () => {
    if (!window.confirm(`Eliminare la prenotazione ${r?.code}?`)) return;
    try {
      await api.delete(`/admin/reservations/${id}`);
      toast.success("Eliminata");
      onChanged && onChanged();
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const copyLink = async () => {
    if (!r?.confirm_url) return;
    try {
      await navigator.clipboard.writeText(r.confirm_url);
      toast.success("Link copiato");
    } catch {
      window.prompt("Copia il link (Ctrl+C):", r.confirm_url);
    }
  };

  const [sending, setSending] = useState(false);
  const resend = async () => {
    if (sending) { return; }
    setSending(true);
    try {
      const rr = await api.post(`/admin/reservations/${id}/resend`, { client_request_id: `ui-${id}-${Date.now()}` });
      const n = rr.data?.notification;
      if (n?.status === "accepted") { toast.success("Richiesta accettata da Meta: esito in arrivo"); }
      else { toast.error(n?.error || "Invio non riuscito"); }
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSending(false); }
  };

  // Merged timeline: status_history + updates_history, newest first.
  const timeline = useMemo(() => {
    if (!r) return [];
    const items = [];
    for (const h of r.status_history || []) {
      items.push({ kind: "status", at: h.at, label: STATUS_LABELS[h.status] || h.status,
                   actor: h.actor_name || h.actor_email, note: h.note, forced: h.forced });
    }
    for (const h of r.updates_history || []) {
      const lbl = (v) => (h.field === "booking_source"
        ? ((cfg?.origins || []).find((o) => o.key === v)?.label || fmtVal(v))
        : h.field === "customer_id" ? (v ? "cliente collegato" : "—") : fmtVal(v));
      items.push({
        kind: "field", at: h.at,
        label: `${FIELD_LABELS[h.field] || h.field}: ${lbl(h.from)} → ${lbl(h.to)}`,
        actor: h.actor_name || h.actor_email,
      });
    }
    return items.sort((a, b) => (b.at || "").localeCompare(a.at || ""));
  }, [r, cfg]);

  return (
    <Sheet open onOpenChange={onClose}>
      <SheetContent side="right"
                      className="w-full sm:max-w-[560px] p-0 overflow-hidden flex flex-col"
                      data-testid="resv-edit-drawer">
        {loading || !form ? (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-ink-400" />
          </div>
        ) : (
          <>
            <SheetHeader className="pl-6 pr-14 py-4 border-b border-sand-200 flex-shrink-0">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <SheetTitle className="flex items-center gap-2 font-sans">
                    <span>{r.customer_name} {r.customer_surname || ""}</span>
                    <span className={`text-[10px] rounded-full border px-2 py-0.5 ${STATUS_STYLES[r.status]}`}
                            data-testid="resv-edit-status-badge">
                      {STATUS_LABELS[r.status]}
                    </span>
                  </SheetTitle>
                  <SheetDescription className="font-mono text-xs tabular-nums">
                    {r.code}
                  </SheetDescription>
                  <div className="text-[11px] text-ink-500 mt-1" data-testid="resv-created-by">
                    Inserita da <b className="text-ink-800">{r.created_by_name || r.created_by || "non registrato"}</b>
                    {r.created_at && <> · il <span className="tabular-nums">{fmtRome(r.created_at)}</span></>}
                  </div>
                </div>
                {/* Change-status inline */}
                <select
                  data-testid="resv-edit-status-select"
                  value={r.status}
                  disabled={!canEdit || (!canCancel && r.status === "annullata")}
                  onChange={(e) => changeStatus(e.target.value)}
                  className="shrink-0 text-xs rounded-md border border-sand-300 bg-white px-2 py-1 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed">
                  {Object.entries(STATUS_LABELS).filter(([k]) => canCancel || k !== "annullata" || r.status === "annullata").map(([k, l]) => (
                    <option key={k} value={k}>{l}</option>
                  ))}
                </select>
              </div>
            </SheetHeader>

            <div className="flex-1 overflow-y-auto px-6 py-4" data-testid="resv-edit-body">
            <fieldset disabled={!canEdit} className="space-y-6 min-w-0 border-0 p-0 m-0 disabled:[&_input]:bg-sand-50 disabled:[&_textarea]:bg-sand-50 disabled:[&_select]:bg-sand-50"
                      data-testid="resv-edit-fieldset">

              {/* Cliente */}
              <section>
                <h3 className="text-xs uppercase tracking-wider text-ink-500 mb-2 flex items-center gap-1">
                  <User className="w-3 h-3" /> Cliente
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Nome*">
                    <Input value={form.customer_name}
                            onChange={(e) => set("customer_name")(e.target.value)}
                            data-testid="edit-name" />
                  </Field>
                  <Field label="Cognome">
                    <Input value={form.customer_surname}
                            onChange={(e) => set("customer_surname")(e.target.value)}
                            data-testid="edit-surname" />
                  </Field>
                  <Field label="Telefono*" full>
                    <Input value={form.customer_phone}
                            onChange={(e) => set("customer_phone")(e.target.value)}
                            className="font-sans tabular-nums"
                            data-testid="edit-phone" />
                  </Field>
                  {crmMatch && (
                    <div className="col-span-2 rounded-md border border-emerald-300 bg-emerald-50 p-2 text-xs"
                          data-testid="edit-crm-match">
                      <div className="flex items-center gap-2">
                        <Sparkles className="w-3 h-3 text-emerald-700" />
                        <b>Cliente CRM esistente:</b> {crmMatch.name} {crmMatch.surname || ""}
                        {crmMatch.orders_count > 0 && <span>· {crmMatch.orders_count} ordini</span>}
                        <button onClick={applyCrmPrefill}
                                  data-testid="edit-crm-prefill"
                                  className="ml-auto text-emerald-800 hover:underline">
                          Riusa questi dati
                        </button>
                      </div>
                    </div>
                  )}
                  <Field label="Email" full>
                    <Input type="email" value={form.customer_email}
                            onChange={(e) => set("customer_email")(e.target.value)}
                            data-testid="edit-email" />
                  </Field>
                </div>
              </section>

              {/* Prenotazione */}
              <section>
                <h3 className="text-xs uppercase tracking-wider text-ink-500 mb-2 flex items-center gap-1">
                  <CalendarDays className="w-3 h-3" /> Prenotazione
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Data">
                    <Input type="date" value={form.date}
                            onChange={(e) => set("date")(e.target.value)}
                            className="font-sans tabular-nums"
                            data-testid="edit-date" />
                  </Field>
                  <Field label="Ora">
                    <Input type="time" value={form.time}
                            onChange={(e) => set("time")(e.target.value)}
                            className="font-sans tabular-nums"
                            data-testid="edit-time" />
                  </Field>
                  <Field label="Coperti">
                    <Input type="number" min={1} value={form.pax}
                            onChange={(e) => set("pax")(e.target.value)}
                            className="font-sans tabular-nums"
                            data-testid="edit-pax" />
                  </Field>
                  <Field label="Durata (min)">
                    <Input type="number" min={30} step={15} value={form.duration_min}
                            onChange={(e) => set("duration_min")(e.target.value)}
                            className="font-sans tabular-nums"
                            data-testid="edit-duration" />
                  </Field>
                  <Field label="Zona">
                    <ZonePicker value={form.zone} onChange={set("zone")} size="sm" testid="edit-zone" />
                    {form.zone && !ZONES.includes(form.zone) && (
                      <div className="text-[11px] text-ink-500 mt-1" data-testid="edit-zone-legacy">Valore storico: {form.zone}</div>
                    )}
                  </Field>
                  <Field label="Tavolo">
                    <Input value={form.table_number}
                            onChange={(e) => set("table_number")(e.target.value)}
                            placeholder="es. T5, 12"
                            className="font-sans tabular-nums"
                            data-testid="edit-table" />
                  </Field>
                </div>
              </section>

              {/* Dettagli */}
              <section>
                <h3 className="text-xs uppercase tracking-wider text-ink-500 mb-2 flex items-center gap-1">
                  <StickyNote className="w-3 h-3" /> Dettagli
                </h3>
                <div className="grid grid-cols-1 gap-3">
                  <Field label="Origine">
                    <select value={form.booking_source}
                            onChange={(e) => set("booking_source")(e.target.value)}
                            data-testid="edit-booking-source"
                            className="w-full h-9 rounded-md border border-sand-300 bg-white px-2 text-sm">
                      <option value="">— non indicata —</option>
                      {form.booking_source && !(cfg?.origins || []).some((o) => o.key === form.booking_source) && (
                        <option value={form.booking_source}>{form.booking_source}</option>
                      )}
                      {(cfg?.origins || []).map((o) => (
                        <option key={o.key} value={o.key}>{o.label}</option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Occasione">
                    <Input value={form.occasion}
                            onChange={(e) => set("occasion")(e.target.value)}
                            placeholder="compleanno, anniversario…"
                            data-testid="edit-occasion" />
                  </Field>
                  <Field label="Allergie (separate da virgola)">
                    <Input value={form.allergies}
                            onChange={(e) => set("allergies")(e.target.value)}
                            data-testid="edit-allergies" />
                  </Field>
                  <Field label="Note">
                    <Textarea value={form.notes}
                                onChange={(e) => set("notes")(e.target.value)}
                                rows={3}
                                data-testid="edit-notes" />
                  </Field>
                </div>
              </section>

              {/* Messaggio + link */}
              <ReservationNotificationCard r={r} canEdit={canEdit} onCopy={copyLink} available={Boolean(cfg?.whatsapp_confirm?.available)}
                                           onResend={resend} sending={sending} />

              {/* Timeline */}
              <section>
                <h3 className="text-xs uppercase tracking-wider text-ink-500 mb-2 flex items-center gap-1">
                  <History className="w-3 h-3" /> Cronologia
                </h3>
                <ul className="space-y-1.5" data-testid="edit-timeline">
                  {timeline.map((h, i) => (
                    <li key={i}
                        className={`text-xs pl-2 border-l-2 ${h.kind === "status" ? "border-bronze-400" : "border-sand-300"}`}>
                      <div className={`${h.kind === "status" ? "font-semibold text-ink-800" : "text-ink-600"}`}>
                        {h.kind === "status" && "Stato → "}
                        {h.label}
                        {h.forced && <span className="ml-1 text-[10px] text-rose-700">(forzato)</span>}
                      </div>
                      <div className="text-[10px] text-ink-400 tabular-nums">
                        {fmtWhen(h.at)} {h.actor && <>· {h.actor}</>}
                        {h.note && <> · {h.note}</>}
                      </div>
                    </li>
                  ))}
                  {timeline.length === 0 && (
                    <li className="text-xs text-ink-400 italic">Nessuna modifica registrata.</li>
                  )}
                </ul>
              </section>
            </fieldset>
            </div>

            {/* Footer */}
            <div className="px-6 py-3 border-t border-sand-200 flex-shrink-0 flex items-center justify-between gap-2">
              {canDelete ? (
              <Button variant="outline" size="sm" onClick={del}
                        className="text-rose-800 border-rose-300 hover:bg-rose-50"
                        data-testid="edit-delete">
                <Trash2 className="w-4 h-4 mr-1" /> Elimina
              </Button>
              ) : <span />}
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={onClose}
                          data-testid="edit-cancel">{canEdit ? "Annulla" : "Chiudi"}</Button>
                {canEdit && (
                <Button size="sm" onClick={() => save(false)} disabled={saving}
                          data-testid="edit-save">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                  Salva
                </Button>
                )}
              </div>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <Label className="text-xs text-ink-500">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
